/**
 * Job Application Service
 * Handles all REST API calls for Belvoir Castle job applications.
 *
 * Endpoints (WordPress mu-plugin: job-applications.php):
 *   POST   /custom/v1/job-applications                    — submit application (public)
 *   GET    /custom/v1/job-applications                    — list all (admin)
 *   POST   /custom/v1/job-applications/{id}/status        — update status (admin)
 *   DELETE /custom/v1/job-applications/{id}               — delete (admin)
 *   GET    /custom/v1/job-applications/{id}/document/cv   — download CV (admin)
 *   GET    /custom/v1/job-applications/{id}/document/cover — download cover letter (admin)
 */

import config from '../config/environment';

const API_BASE = `${config.apiEndpoint}/custom/v1`;

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

async function handleResponse(response) {
  const text = await response.text();
  // The server can print PHP notices before the JSON body.
  const jsonStart = text.search(/[[{]/);
  let json;
  try {
    json = JSON.parse(jsonStart > 0 ? text.slice(jsonStart) : text);
  } catch {
    throw new Error(`Server returned non-JSON response (${response.status}): ${text.slice(0, 200)}`);
  }
  if (!response.ok) {
    const message =
      json?.message || json?.data?.message || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(message);
  }
  return json;
}

/**
 * Read a File object as a base64 data URI.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Submit a job application.
 *
 * @param {Object} application
 * @param {string} application.roleId
 * @param {string} application.roleTitle
 * @param {string} application.firstName
 * @param {string} application.lastName
 * @param {string} application.email
 * @param {string} [application.phone]
 * @param {string} [application.portfolio]
 * @param {string} [application.coverNote]
 * @param {string} application.cvData         — base64 data URI
 * @param {string} application.cvFileName     — original filename
 * @param {string} application.coverLetterData    — base64 data URI
 * @param {string} application.coverLetterFileName — original filename
 * @returns {Promise<{ id: number, message: string }>}
 */
export async function submitApplication(application) {
  const response = await fetch(`${API_BASE}/job-applications`, {
    method: 'POST',
    headers,
    body: JSON.stringify(application),
  });
  const json = await handleResponse(response);
  return json.data;
}

/**
 * Fetch all applications (admin).
 * @returns {Promise<Array>}
 */
export async function getAllApplications() {
  const response = await fetch(`${API_BASE}/job-applications`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data ?? [];
}

/**
 * Update the status of an application (admin).
 * @param {number} id
 * @param {string} status — 'pending' | 'reviewing' | 'shortlisted' | 'rejected' | 'offered'
 * @param {string} [notes]
 */
export async function updateApplicationStatus(id, status, notes = '') {
  const response = await fetch(`${API_BASE}/job-applications/${id}/status`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ status, notes }),
  });
  return handleResponse(response);
}

/**
 * Delete an application (admin).
 * @param {number} id
 */
export async function deleteApplication(id) {
  const response = await fetch(`${API_BASE}/job-applications/${id}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
}

/**
 * Fetch a document (CV or cover letter) as base64 (admin).
 * @param {number} id
 * @param {'cv'|'cover'} type
 * @returns {Promise<{ fileName: string, mimeType: string, content: string }>}
 */
export async function getApplicationDocument(id, type) {
  const response = await fetch(`${API_BASE}/job-applications/${id}/document/${type}`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data;
}

/**
 * Open a document in a new browser tab.
 * @param {number} id
 * @param {'cv'|'cover'} type
 */
export async function openApplicationDocument(id, type) {
  const doc = await getApplicationDocument(id, type);
  const byteChars = atob(doc.content);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: doc.mimeType });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
